import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plug,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { integrationsAPI } from '../services/api';
import IntegrationCard from '../components/integrations/IntegrationCard';
import GHLSetupWizard from '../components/integrations/GHLSetupWizard';
import HubSpotSetupWizard from '../components/integrations/HubSpotSetupWizard';

export default function IntegrationsPage() {
  const [searchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWizard, setActiveWizard] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadIntegrations();

    // Check for OAuth callback params
    const hubspotStatus = searchParams.get('hubspot');
    const portalId = searchParams.get('portal_id');
    const error = searchParams.get('error');

    if (hubspotStatus === 'connected' && portalId) {
      toast.success(`HubSpot connected! Portal ID: ${portalId}`);
      // Open the wizard to step 2 (after OAuth)
      setActiveWizard({ type: 'hubspot', step: 2, portalId });
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
    }
  }, [searchParams]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await integrationsAPI.getAll();
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadIntegrations();
    setRefreshing(false);
    toast.success('Integrations refreshed');
  };

  const handleConnect = (type) => {
    setActiveWizard({ type, step: 1 });
  };

  const handleConfigure = (type) => {
    setActiveWizard({ type, step: 1, isReconfigure: true });
  };

  const handleDisconnect = async (type) => {
    if (!confirm(`Are you sure you want to disconnect ${type.toUpperCase()}? This will revoke API access.`)) {
      return;
    }

    try {
      await integrationsAPI.disconnect(type);
      toast.success(`${type.toUpperCase()} disconnected`);
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to disconnect integration');
    }
  };

  const handleWizardComplete = () => {
    setActiveWizard(null);
    loadIntegrations();
  };

  const handleWizardClose = () => {
    setActiveWizard(null);
  };

  // Integration icons/logos
  const getIntegrationLogo = (type) => {
    switch (type) {
      case 'ghl':
        return (
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
            GHL
          </div>
        );
      case 'hubspot':
        return (
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
              <path d="M17.44 11.06c-.24-.5-.6-.89-1.06-1.17-.47-.28-1.01-.42-1.62-.42-.6 0-1.14.14-1.6.42-.47.28-.83.67-1.08 1.17-.25.5-.38 1.08-.38 1.73s.13 1.23.38 1.73c.25.5.61.89 1.08 1.17.46.28 1 .42 1.6.42.61 0 1.15-.14 1.62-.42.46-.28.82-.67 1.06-1.17.24-.5.36-1.08.36-1.73s-.12-1.23-.36-1.73zm-2.68 4.3c-.36.19-.76.28-1.2.28-.44 0-.84-.09-1.2-.28-.35-.19-.63-.46-.83-.81-.2-.36-.3-.78-.3-1.26s.1-.9.3-1.26c.2-.35.48-.62.83-.81.36-.19.76-.28 1.2-.28.44 0 .84.09 1.2.28.35.19.63.46.83.81.2.36.3.78.3 1.26s-.1.9-.3 1.26c-.2.35-.48.62-.83.81z"/>
              <path d="M19.5 9.5V5c0-1.1-.9-2-2-2h-11c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2v-4.5l2.5 2.5V7l-2.5 2.5z"/>
            </svg>
          </div>
        );
      case 'zapier':
        return (
          <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="white" className="w-7 h-7">
              <path d="M12 2L4 6v12l8 4 8-4V6l-8-4zm0 2.18l5.45 2.73L12 9.64 6.55 6.91 12 4.18zM6 8.27l5 2.5v6.96l-5-2.5V8.27zm12 6.96l-5 2.5v-6.96l5-2.5v6.96z"/>
            </svg>
          </div>
        );
      case 'salesforce':
        return (
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
            SF
          </div>
        );
      default:
        return (
          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
            <Plug className="w-6 h-6 text-gray-500" />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="w-6 h-6" />
            Integrations
          </h1>
          <p className="text-gray-500 mt-1">
            Connect Alfred Voice with your CRM and automation tools
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Integration Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.type}
            integration={integration}
            logo={getIntegrationLogo(integration.type)}
            onConnect={() => handleConnect(integration.type)}
            onConfigure={() => handleConfigure(integration.type)}
            onDisconnect={() => handleDisconnect(integration.type)}
          />
        ))}
      </div>

      {/* Empty State */}
      {integrations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Plug className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations available</h3>
          <p className="text-gray-500">Contact support to enable integrations for your account.</p>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-12 p-6 bg-gray-50 rounded-lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="https://docs.alfredvoice.ai/integrations/ghl"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-4 bg-white rounded-lg border hover:border-gray-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">GHL Setup Guide</span>
          </a>
          <a
            href="https://docs.alfredvoice.ai/integrations/hubspot"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-4 bg-white rounded-lg border hover:border-gray-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">HubSpot Setup Guide</span>
          </a>
          <a
            href="mailto:support@alfredvoice.ai"
            className="flex items-center gap-2 p-4 bg-white rounded-lg border hover:border-gray-300 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-gray-400" />
            <span className="text-gray-700">Contact Support</span>
          </a>
        </div>
      </div>

      {/* Setup Wizards */}
      {activeWizard?.type === 'ghl' && (
        <GHLSetupWizard
          isOpen={true}
          onClose={handleWizardClose}
          onComplete={handleWizardComplete}
          isReconfigure={activeWizard.isReconfigure}
        />
      )}

      {activeWizard?.type === 'hubspot' && (
        <HubSpotSetupWizard
          isOpen={true}
          onClose={handleWizardClose}
          onComplete={handleWizardComplete}
          initialStep={activeWizard.step}
          portalId={activeWizard.portalId}
          isReconfigure={activeWizard.isReconfigure}
        />
      )}
    </div>
  );
}
